import { z } from "zod";
import { polyArea, type Pt } from "./geometry.js";

/**
 * Schaal van een tekening (per verdieping): één of twee bekende maten, uitgezet tussen twee punten.
 * Punten zijn genormaliseerd 0–1 zoals ruimtes; `aspect` (hoogte/breedte van het blad) legt de
 * verhouding vast waarin ze zijn aangeklikt.
 */
const coord = z.number().min(0).max(1);
export const scaleLineSchema = z.object({
  a: z.tuple([coord, coord]),
  b: z.tuple([coord, coord]),
  /** Werkelijke lengte in meter. */
  meters: z.number().positive().max(10_000),
});
export const floorScaleSchema = z.object({
  aspect: z.number().positive().max(100),
  lines: z.array(scaleLineSchema).min(1).max(2),
});
export type ScaleLine = z.infer<typeof scaleLineSchema>;
export type FloorScale = z.infer<typeof floorScaleSchema>;

/** Werkelijke afmetingen van het hele blad in meter. */
export interface PlanDims {
  w: number;
  h: number;
}

/** Kleinste hoek (graden) tussen twee maten om breedte en hoogte los te schalen. */
const MIN_ANGLE = 30;

/** Breedte van het blad (m) als de tekening gelijk geschaald is (één maat). */
function uniformWidth(l: ScaleLine, aspect: number): number | null {
  const dx = l.b[0] - l.a[0];
  const dy = (l.b[1] - l.a[1]) * aspect;
  const len = Math.hypot(dx, dy);
  return len > 1e-6 ? l.meters / len : null;
}

/** Hoek (0–90°) tussen twee maten zoals ze op de tekening staan. */
function angleBetween(p: ScaleLine, q: ScaleLine, aspect: number): number {
  const dir = (l: ScaleLine) => Math.atan2((l.b[1] - l.a[1]) * aspect, l.b[0] - l.a[0]);
  const d = Math.abs(dir(p) - dir(q)) % Math.PI;
  return (Math.min(d, Math.PI - d) * 180) / Math.PI;
}

/**
 * Afmetingen van het blad. Eén maat: gelijk geschaald. Twee maten in verschillende richting:
 * breedte en hoogte los, zodat een scheef uitgerekte scan of foto toch klopt.
 */
export function planDims(scale: FloorScale | null | undefined): PlanDims | null {
  if (!scale) return null;
  const { aspect, lines } = scale;
  const ws = lines.map((l) => uniformWidth(l, aspect));
  if (ws.some((w) => w == null)) return null;
  if (lines.length === 2 && angleBetween(lines[0]!, lines[1]!, aspect) >= MIN_ANGLE) {
    // (dx·W)² + (dy·H)² = L² voor beide maten: twee vergelijkingen in W² en H².
    const [p, q] = lines as [ScaleLine, ScaleLine];
    const a1 = (p.b[0] - p.a[0]) ** 2;
    const b1 = (p.b[1] - p.a[1]) ** 2;
    const a2 = (q.b[0] - q.a[0]) ** 2;
    const b2 = (q.b[1] - q.a[1]) ** 2;
    const det = a1 * b2 - a2 * b1;
    if (Math.abs(det) > 1e-12) {
      const w2 = (p.meters ** 2 * b2 - q.meters ** 2 * b1) / det;
      const h2 = (a1 * q.meters ** 2 - a2 * p.meters ** 2) / det;
      if (w2 > 0 && h2 > 0) return { w: Math.sqrt(w2), h: Math.sqrt(h2) };
    }
  }
  const w = (ws as number[]).reduce((s, v) => s + v, 0) / ws.length;
  return { w, h: w * aspect };
}

/**
 * Hoeveel twee maten elkaar tegenspreken als de tekening gelijk geschaald zou zijn (0,03 = 3 %).
 * Groot verschil: verkeerd aangeklikt of ingevuld, of de tekening is scheef uitgerekt.
 */
export function scaleDeviation(scale: FloorScale | null | undefined): number | null {
  if (!scale || scale.lines.length < 2) return null;
  const [w1, w2] = scale.lines.map((l) => uniformWidth(l, scale.aspect));
  if (!w1 || !w2) return null;
  return Math.abs(w1 - w2) / ((w1 + w2) / 2);
}

/** Lengte van een lijn op de tekening in meter. */
export function lineMeters(a: Pt, b: Pt, dims: PlanDims): number {
  return Math.hypot((b[0] - a[0]) * dims.w, (b[1] - a[1]) * dims.h);
}

/** Oppervlakte van een ingetekend vlak in m². */
export function shapeM2(points: readonly Pt[], dims: PlanDims): number {
  return polyArea(points) * dims.w * dims.h;
}

/** Oppervlakte per ruimte (m², null zonder schaal of vlak) en het totaal van een verdieping. */
export function floorAreas<R extends { id: string; shape: { points: Pt[] } | null }>(
  scale: FloorScale | null | undefined,
  rooms: readonly R[],
): { byRoom: Map<string, number | null>; total: number; measured: number; scaled: boolean } {
  const dims = planDims(scale);
  const byRoom = new Map<string, number | null>();
  let total = 0;
  let measured = 0;
  for (const r of rooms) {
    const m2 = dims && r.shape ? shapeM2(r.shape.points, dims) : null;
    byRoom.set(r.id, m2);
    if (m2 != null) {
      total += m2;
      measured++;
    }
  }
  return { byRoom, total, measured, scaled: Boolean(dims) };
}

/** Oppervlakte van het gebouw: som van alle ingetekende ruimtes op verdiepingen met een schaal. */
export function buildingArea(
  floors: readonly { id: string; scale: FloorScale | null }[],
  rooms: readonly { id: string; floorId: string; shape: { points: Pt[] } | null }[],
): { total: number; rooms: number; measured: number; floors: number; scaledFloors: number } {
  let total = 0;
  let measured = 0;
  let scaledFloors = 0;
  for (const f of floors) {
    const a = floorAreas(
      f.scale,
      rooms.filter((r) => r.floorId === f.id),
    );
    total += a.total;
    measured += a.measured;
    if (a.scaled) scaledFloors++;
  }
  return { total, rooms: rooms.length, measured, floors: floors.length, scaledFloors };
}

/** "12,5 m²" (onder 100 m² één decimaal, daarboven afgerond). */
export function formatM2(m2: number): string {
  const n = m2 < 100 ? Math.round(m2 * 10) / 10 : Math.round(m2);
  return `${n.toLocaleString("nl-NL", { maximumFractionDigits: 1 })} m²`;
}

/** "24,6 m" (twee decimalen onder 10 m). */
export function formatMeters(m: number): string {
  return `${m.toLocaleString("nl-NL", { maximumFractionDigits: m < 10 ? 2 : 1 })} m`;
}
